import { useMemo } from 'react';
import { CRM_OBJECT } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { useFetchBatchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { STATUS_LOADING } from 'reference-resolvers-lite/Status';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { CRM_OBJECT_TYPE_IDS } from 'personalization-lib/constants/objectTypeIds';
export const getCrmObjectKey = ({
  objectTypeId,
  objectId
}) => `${objectTypeId}:${objectId}`;
const getObjectIds = (targets, objectTypeId) => {
  const objectIds = Array.from(new Set(targets.filter(target => target.objectTypeId === objectTypeId).map(target => String(target.objectId))));

  // The resolver only fetches when it is given ids, so stay idle when nothing is targeted
  return objectIds.length ? objectIds : undefined;
};
const toCrmObjectNames = (objectTypeId, references = []) => Object.fromEntries(references.map(({
  id,
  label
}) => [getCrmObjectKey({
  objectTypeId,
  objectId: Number(id)
}), label]));
const useCrmObjectReferencesByType = (objectTypeId, objectIds) => {
  const resolver = useMemo(() => createStandardCachedResolver(CRM_OBJECT, objectTypeId), [objectTypeId]);
  const {
    references,
    status
  } = useFetchBatchResolver(resolver, objectIds);
  return useMemo(() => ({
    names: toCrmObjectNames(objectTypeId, references),
    loading: status === STATUS_LOADING
  }), [objectTypeId, references, status]);
};

/**
 * Resolves the display names of the CRM objects that variants are targeted at, keyed by
 * `objectTypeId:objectId`. Only the CRM object types that variants can be targeted at (contacts and
 * companies) are resolved, and targets that cannot be resolved are left out of the map so callers
 * can fall back, the same way `CrmObjectName` renders nothing when a target is not found.
 *
 * Images are deliberately not resolved here: `CrmObjectAvatar` renders them through the shared
 * avatar service, which resolves a company logo or contact avatar from the record itself and falls
 * back to an initialled placeholder, rather than from a single CRM property that is usually unset.
 */
const useCrmObjectNames = targets => {
  const companyIds = useMemo(() => getObjectIds(targets, CRM_OBJECT_TYPE_IDS.COMPANY), [targets]);
  const contactIds = useMemo(() => getObjectIds(targets, CRM_OBJECT_TYPE_IDS.CONTACT), [targets]);
  const companies = useCrmObjectReferencesByType(CRM_OBJECT_TYPE_IDS.COMPANY, companyIds);
  const contacts = useCrmObjectReferencesByType(CRM_OBJECT_TYPE_IDS.CONTACT, contactIds);
  return useMemo(() => ({
    names: Object.assign({}, companies.names, contacts.names),
    loading: companies.loading || contacts.loading
  }), [companies, contacts]);
};
export default useCrmObjectNames;